import { useEffect, useId, useState } from 'react'

export type CharacterCountProps = {
  label: string
  maxLength: number
  hint?: string
  value?: string
  onChange?: (value: string) => void
  /** A single-line input instead of a text area. */
  singleLine?: boolean
  id?: string
}

/**
 * A text field that shows how many characters remain. Typing past the limit is
 * allowed, so nothing is cut off; the count turns into an error instead. The
 * spoken count waits until typing pauses, so it does not interrupt.
 */
export default function CharacterCount({ label, maxLength, hint, value = '', onChange, singleLine = false, id }: CharacterCountProps) {
  const autoId = useId()
  const fieldId = id ?? autoId
  const hintId = `${fieldId}-hint`
  const countId = `${fieldId}-count`
  const [text, setText] = useState(value)
  // A new value from the parent, such as a reset, replaces what is typed.
  const [given, setGiven] = useState(value)
  if (value !== given) {
    setGiven(value)
    setText(value)
  }
  const left = maxLength - text.length
  const over = left < 0
  const message = over
    ? `${-left} ${-left === 1 ? 'character' : 'characters'} over limit`
    : `${left} ${left === 1 ? 'character' : 'characters'} left`
  const [spoken, setSpoken] = useState(message)
  useEffect(() => {
    const timer = window.setTimeout(() => setSpoken(message), 700)
    return () => window.clearTimeout(timer)
  }, [message])

  const shared = {
    id: fieldId,
    className: singleLine ? 'mds-input' : 'mds-textarea',
    value: text,
    'aria-invalid': over,
    'aria-describedby': [hint ? hintId : '', countId].filter(Boolean).join(' '),
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setText(event.target.value)
      onChange?.(event.target.value)
    },
  }

  return (
    <div className="mds-field mds-count">
      <label className="mds-label" htmlFor={fieldId}>
        {label}
      </label>
      {hint && (
        <span className="mds-hint" id={hintId}>
          {hint}
        </span>
      )}
      {singleLine ? <input {...shared} /> : <textarea rows={4} {...shared} />}
      <span className={`mds-count__message${over ? ' is-over' : ''}`} id={countId} aria-hidden="true">
        {message}
      </span>
      <span className="mds-visually-hidden" aria-live="polite">
        {spoken}
      </span>
    </div>
  )
}
